/**
 * How each onboarding activity type is shown. The server stores only the type key,
 * so wording and icons can change here without touching saved history.
 */

/** Icon chip colours, matched to the status palette used on the list page. */
export var ACTIVITY_TONES = {
  brand: { bg: "#E7F1FF", color: "#0011D3" },
  neutral: { bg: "#F1F2F4", color: "#5C6670" },
  success: { bg: "#EAFFE4", color: "#078A00" },
  danger: { bg: "#FFF0F1", color: "#E5384A" },
  warning: { bg: "#FFF9EC", color: "#E07B00" },
  violet: { bg: "#F3F0FF", color: "#833AFF" },
  teal: { bg: "#EEFFFB", color: "#00877F" },
};

/**
 * `prefix` / `suffix` wrap the form name (shown as a link); types without a form
 * use `prefix` alone. `detail` is the muted line under the action.
 */
export var ACTIVITY_TYPES = {
  "candidate.onboarded": {
    icon: "UserPlus",
    tone: "brand",
    prefix: "Onboarded the candidate",
    detail: "Login details emailed to the candidate.",
  },
  "candidate.updated": { icon: "PencilSimple", tone: "neutral", prefix: "Updated candidate details" },
  "candidate.login_enabled": { icon: "LockSimpleOpen", tone: "success", prefix: "Enabled candidate login" },
  "candidate.login_disabled": { icon: "LockSimple", tone: "danger", prefix: "Disabled candidate login" },
  "form.draft_saved": { icon: "FloppyDisk", tone: "neutral", prefix: "Saved a draft of" },
  "form.section_completed": { icon: "ListChecks", tone: "teal", prefix: "Completed" },
  "form.submitted": {
    icon: "PaperPlaneTilt",
    tone: "violet",
    prefix: "Submitted",
    detail: "Form submitted for HR review.",
  },
  "form.resubmitted": {
    icon: "PaperPlaneTilt",
    tone: "violet",
    prefix: "Re-submitted",
    detail: "Updated after requested changes and sent for HR review.",
  },
  "review.approved": { icon: "CheckCircle", tone: "success", prefix: "Approved" },
  "review.resubmit_requested": { icon: "ArrowClockwise", tone: "warning", prefix: "Requested changes on" },
  "review.reopened": { icon: "ArrowUUpLeft", tone: "warning", prefix: "Reopened", suffix: "for changes" },
  "milestone.all_submitted": {
    icon: "ListMagnifyingGlass",
    tone: "violet",
    prefix: "All forms submitted",
    detail: "Ready for HR review.",
  },
  "milestone.completed": {
    icon: "CheckSquare",
    tone: "success",
    prefix: "Onboarding completed",
    detail: "All 7 forms approved.",
  },
};

var FALLBACK_TYPE = { icon: "ListChecks", tone: "neutral", prefix: "Updated" };

/** New Joinee Form steps, keyed as the server records them. */
export var NEW_JOINEE_SECTION_LABELS = {
  candidate: "Candidate Info",
  employment: "Employment",
  education: "Education",
  bank: "Bank Details",
  insurance: "Insurance",
};

var ROLE_LABELS = { hr: "HR", candidate: "Candidate", super_admin: "Super Admin" };

export function activityTypeMeta(type) {
  var meta = ACTIVITY_TYPES[type] || FALLBACK_TYPE;
  return Object.assign({}, meta, { tone: ACTIVITY_TONES[meta.tone] || ACTIVITY_TONES.neutral });
}

/** Action line split around the form name: { prefix, form, suffix }. */
export function activityActionParts(activity) {
  var meta = ACTIVITY_TYPES[activity && activity.type] || FALLBACK_TYPE;
  var form = activity && activity.form && activity.form.label ? activity.form : null;
  if (activity && activity.type === "form.section_completed") {
    var section = NEW_JOINEE_SECTION_LABELS[activity.section] || "a step";
    return { prefix: "Completed " + section + " in", form: form, suffix: "" };
  }
  return { prefix: meta.prefix, form: form, suffix: meta.suffix || "" };
}

/** "Priya Sharma" + "HR"; system events have no role. */
export function activityActor(activity) {
  var actor = (activity && activity.actor) || {};
  if (actor.role === "system") return { name: "System", role: "" };
  return { name: actor.name || "Unknown user", role: ROLE_LABELS[actor.role] || "" };
}

var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var TIME_ZONE = "Asia/Kolkata";

function istParts(value) {
  var date = new Date(value);
  if (isNaN(date.getTime())) return null;
  var parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .formatToParts(date)
    .forEach(function (part) {
      parts[part.type] = part.value;
    });
  return parts;
}

/** "30 Sep 2026" in IST. */
export function formatActivityDay(value) {
  var p = istParts(value);
  if (!p) return "";
  return Number(p.day) + " " + MONTHS[Number(p.month) - 1] + " " + p.year;
}

/** "10:45 AM" in IST. */
export function formatActivityTime(value) {
  var p = istParts(value);
  if (!p) return "";
  return p.hour + ":" + p.minute + " " + String(p.dayPeriod || "").toUpperCase();
}

/** Newest-first activities grouped under their IST day, keeping order. */
export function groupActivitiesByDay(activities) {
  var groups = [];
  (activities || []).forEach(function (activity) {
    var day = formatActivityDay(activity.createdAt);
    var last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(activity);
    } else {
      groups.push({ day: day, items: [activity] });
    }
  });
  return groups;
}
